import { CustomerGroupType } from "@redotech/redo-model/customers/customer-group-definition";
import {
  AnnualTimeframeCondition,
  FullDateTimeframeCondition,
  TimeframeUnit,
} from "@redotech/redo-model/marketing/segments/segment-timeframe";
import {
  ConstantActivityCount,
  CustomerActivityType,
  CustomerCharacteristicType,
  NTimesActivityCount,
  SegmentConditionType,
  SegmentSubscriberStatistics,
} from "@redotech/redo-model/marketing/segments/segment-types";
import { ConjunctionMode } from "@redotech/redo-model/segments/segment-types";
import {
  BooleanCondition,
  CityProximityCondition,
  NumericCompare,
  StringCompare,
  TokenCondition,
  TokenHierarchyCondition,
  TokenListCondition,
  WhereConditionDataType,
} from "@redotech/redo-model/segments/segment-where-condition";
import { PartialExcept, PartialFields } from "@redotech/util/type";

export interface SegmentInProgress {
  name: string;
  type: CustomerGroupType;
  lastUpdated?: Date;
  mode: ConjunctionMode;
  conditionBlocks: SegmentConditionBlockInProgress[];
  initialStatistics?: SegmentSubscriberStatistics;
}

export interface SegmentConditionBlockInProgress {
  mode: ConjunctionMode;
  conditions: SegmentConditionInProgress[];
}

export type SegmentConditionInProgress =
  | { type: "empty" }
  | CustomerAttributeConditionInProgress
  | CustomerActivityConditionInProgress
  | CustomEventConditionInProgress;

export function isSegmentConditionUninitialized(
  segmentCondition: SegmentConditionInProgress,
): segmentCondition is { type: "empty" } {
  return segmentCondition.type === "empty";
}

export interface CustomerAttributeConditionInProgress {
  type: SegmentConditionType.CUSTOMER_ATTRIBUTE;
  whereCondition?: WhereConditionInProgress<CustomerCharacteristicType>;
}

export interface CustomerActivityConditionInProgress {
  type: SegmentConditionType.CUSTOMER_ACTIVITY;
  activityType?: CustomerActivityType;
  count: ActivityCountInProgress;
  timeframe: FullDateTimeFrameConditionInProgress<FullDateTimeframeCondition>;
  whereConditions: WhereConditionInProgress<string>[];
}

export interface CustomEventConditionInProgress {
  type: SegmentConditionType.CUSTOM_EVENT;
  eventName?: string;
  count: ActivityCountInProgress;
  timeframe: FullDateTimeFrameConditionInProgress<FullDateTimeframeCondition>;
  propertyFilters: WhereConditionInProgress<string>[];
}

export type ActivityCountInProgress =
  | ConstantActivityCount
  | PartialExcept<NTimesActivityCount, "type">;

export interface NumericConditionInProgress<D extends string> {
  type: WhereConditionDataType.NUMERIC;
  dimension: D;
  comparison: NumericCompareInProgress;
}

export interface FullDateConditionInProgress<D extends string> {
  type: WhereConditionDataType.DATE_FULL;
  dimension: D;
  comparison: FullDateTimeFrameConditionInProgress<FullDateTimeframeCondition>;
}

export interface AnnualDateConditionInProgress<D extends string> {
  type: WhereConditionDataType.DATE_ANNUAL;
  dimension: D;
  comparison: AnnualTimeframeConditionInProgress<AnnualTimeframeCondition>;
}

export interface CustomFieldConditionInProgress<D extends string> {
  type: WhereConditionDataType.CUSTOM_FIELD;
  dimension: D;
  declarationId: string;
  comparison: (
    | TokenCondition<D>
    | NumericConditionInProgress<D>
    | BooleanCondition<D>
    | FullDateConditionInProgress<D>
  )["comparison"];
}

export interface StringConditionInProgress<D extends string> {
  type: WhereConditionDataType.STRING;
  dimension: D;
  comparison: StringCompareInProgress;
}

export type WhereConditionInProgress<D extends string> =
  | TokenCondition<D>
  | NumericConditionInProgress<D>
  | BooleanCondition<D>
  | FullDateConditionInProgress<D>
  | AnnualDateConditionInProgress<D>
  | TokenListCondition<D>
  | TokenHierarchyCondition<D>
  | CityProximityCondition<D>
  | CustomFieldConditionInProgress<D>
  | StringConditionInProgress<D>;

export type FullDateTimeFrameConditionInProgress<
  T extends FullDateTimeframeCondition,
> = T extends { units: TimeframeUnit }
  ? PartialExcept<T, "type" | "units">
  : PartialExcept<T, "type">;

export type AnnualTimeframeConditionInProgress<
  T extends AnnualTimeframeCondition,
> = T extends { units: TimeframeUnit }
  ? PartialExcept<T, "type" | "units">
  : PartialExcept<T, "type">;

export type NumericCompareInProgress = PartialFields<NumericCompare, "value">;

export type StringCompareInProgress = PartialFields<StringCompare, "value">;
